/** What a learner sees when they open content their account doesn't own.
 *
 * The API answers a gated request with 403 + `X-Required-Module`
 * (e.g. "content.a2.practice"). Showing "Something went wrong" for that is a
 * lie — nothing went wrong, they simply haven't got that level yet. This
 * names the exact level, and links straight to the Store with the right pack
 * pre-selected.
 */
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useT } from "../i18n";

/** "content.a2.practice" → { level: "A2", kind: "practice" }. Returns null
 *  for anything that isn't a content module, so callers can fall back. */
export function parseContentModule(
  code: string | null | undefined,
): { level: string; kind: "learn" | "practice" } | null {
  if (!code) return null;
  const m = /^content\.([a-z][0-9])\.(learn|practice)$/.exec(code);
  if (!m) return null;
  return { level: m[1].toUpperCase(), kind: m[2] as "learn" | "practice" };
}

export function ContentLocked({ module }: { module: string | null }) {
  const t = useT();
  const { user } = useAuth();
  const parsed = parseContentModule(module);
  // Only the account owner can buy. A school pupil must not be pointed at a
  // Store that will refuse them — they're told who to ask instead.
  const canOrder = user?.role === "tenant_admin";

  // A gate we can't name (shouldn't happen) still gets an honest message
  // rather than a wrong one.
  if (!parsed) {
    return (
      <div role="alert" className="rounded-card border border-line bg-surface p-6">
        <p className="font-semibold text-ink">{t("locked.genericTitle")}</p>
        <p className="mt-1 text-sm text-muted">{t("locked.genericBody")}</p>
        {canOrder ? (
          <Link
            to="/store"
            className="mt-4 inline-block rounded-control bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark"
          >
            {t("locked.cta")} →
          </Link>
        ) : (
          <p className="mt-3 text-sm font-semibold text-ink">{t("locked.askAdminGeneric")}</p>
        )}
      </div>
    );
  }

  const kindLabel = t(
    parsed.kind === "practice" ? "store.kind.practice" : "store.kind.learn",
  );
  return (
    <div role="alert" className="rounded-card border border-bronze bg-surface p-6">
      <p className="text-2xl" aria-hidden>🔒</p>
      <h2 className="mt-2 text-lg font-extrabold text-ink">
        {t("locked.title", { level: parsed.level, kind: kindLabel })}
      </h2>
      <p className="mt-1 max-w-prose text-sm text-muted">
        {t("locked.body", { level: parsed.level, kind: kindLabel })}
      </p>
      {canOrder ? (
        <Link
          to={`/store?rec=pack.${parsed.level.toLowerCase()}.${parsed.kind}`}
          className="mt-4 inline-block rounded-control bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark"
        >
          {t("locked.cta")} →
        </Link>
      ) : (
        <p className="mt-3 text-sm font-semibold text-ink">
          {t("locked.askAdmin", { level: parsed.level, kind: kindLabel })}
        </p>
      )}
    </div>
  );
}
